import React, { useContext } from "react";
import { Box, Text, Flex } from "@chakra-ui/react";
import { useParams } from 'react-router-dom';
import { FiClock } from 'react-icons/fi';

import { getStationName, getParameterDisplayName, getRelativeTime, getLatestObservation, getObservationInPreferredUnits, getParameterUnit } from "../../utils/helpers";
import useDatasetsMetadata from "../../components/customHooks/useDatasetsMetadata";
import useObservationData from "../../components/customHooks/useObservationData";
import { ParameterChart } from "../../components/stations/ParameterChart";
import Loading from "../../components/Loading";
import { PreferredUnitsContext } from "../../components/contexts/PreferredUnitsContext";
import { CardinalDirectionIndicator } from "../../components/CardinalDirectionIndicator";
import { getRegionName } from "../../utils/helpers";
import useRegions from "../../components/customHooks/useRegions";
import Observation from "../../components/stations/Observation";

export const Parameter = () => {
  const { datasetId, parameter } = useParams();
  const [preferredUnitSystem] = useContext(PreferredUnitsContext);

  const datasetsMetadata = useDatasetsMetadata();
  const observationData = useObservationData();
  const regions = useRegions();

  const station = datasetsMetadata.data?.find((dataset) => dataset?.datasetID === datasetId);
  const stationMetadata = station?.metadata;
  const stationName = getStationName(stationMetadata);
  const parameterName = getParameterDisplayName(stationMetadata, parameter);
  const stationData = observationData.data?.find((dataset) => dataset?.datasetID === datasetId);
  const parameterObservations = stationData?.obsData
    ?.filter((observation) => observation[parameter])
    .map((observation) => ({
      [parameter]: observation[parameter],
      time: observation.time,
    }));

  const latestObservation = getLatestObservation(parameterObservations, parameter);
  const unit = getParameterUnit(stationMetadata, parameter);
  const parameterDisplay = getObservationInPreferredUnits(parameter, latestObservation?.[parameter], unit, preferredUnitSystem);

  return (
    <Box>
      {stationName && (
        <Box p={4}>
          <Text fontWeight={400} fontSize={14} opacity={'80%'} color='gray.700' textAlign='left' marginBottom={1}>
              {getRegionName(station.regionID, regions.data?.regions)}
            </Text>
          <Text fontWeight={700} fontSize={20} color='gray.700' textAlign='left'>
            {stationName}
          </Text>
        </Box>
      )}
      <Box px={4}>
        <Text fontSize={16} fontWeight={600} color='gray.700' textAlign='left'>
          {parameterName}
        </Text>
        {(datasetsMetadata.isLoading || observationData.isLoading) && <Loading />}
        {parameterObservations?.length > 0 && stationMetadata && (
          <>
            <Text fontWeight={600} fontSize={24} color='gray.700' textAlign='left'>
              <Flex>
                <Observation value={parameterDisplay.value} unit={parameterDisplay.unit} showUnit={true} />
                {unit === '°' &&
                  <CardinalDirectionIndicator degrees={parameterDisplay.value} directionSize={20} arrowSize={25} />
                }
              </Flex>
            </Text>
            <Text fontSize={14} color='gray.700' display='flex' alignItems='center'>
              <FiClock style={{ marginRight: 5 }} />
              {getRelativeTime(latestObservation.time)}
            </Text>
          </>
        )}
      </Box>
      <Box pt={4}>
        {observationData.isSuccess && parameterObservations?.length > 0
          && <ParameterChart parameterObservations={parameterObservations} parameter={parameter} stationMetadata={stationMetadata} simpleChart={false} preferredUnitSystem={preferredUnitSystem} />
        }
      </Box>
    </Box>
  );
};
